import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  TextField,
} from '@mui/material'
import { AssignableQuestionnaire } from 'src/graphql.generated'
import { describeFailure, Failure } from '../api/failureReason'
import FailureMessage from './FailureMessage'
import { formatDays } from '../format'
import DialogHeader from './DialogHeader'
import { BUILT_IN_QUESTIONNAIRES, QUESTIONNAIRE_LABELS } from './questionnaireLabels'

const DEFAULT_REPEAT_CADENCE_IN_DAYS: Record<AssignableQuestionnaire, number> = {
  [AssignableQuestionnaire.Asrm]: 7,
  [AssignableQuestionnaire.Isi]: 14,
  [AssignableQuestionnaire.Ocir]: 28,
  [AssignableQuestionnaire.Pcl5]: 28,
}

type Props = {
  open: boolean
  onClose: () => void
  onAssign: (questionnaire: AssignableQuestionnaire, repeatCadenceInDays?: number) => Promise<void>
  openQuestionnaires: AssignableQuestionnaire[]
}

const AddSymptomSurveyDialog = ({ open, onClose, onAssign, openQuestionnaires }: Props) => {
  const [survey, setSurvey] = useState<AssignableQuestionnaire | ''>('')
  const [error, setError] = useState<Failure | null>(null)
  const [saving, setSaving] = useState(false)
  const [repeatCadenceInDays, setRepeatCadenceInDays] = useState(0)

  const reset = useCallback(() => {
    setSurvey('')
    setError(null)
    setSaving(false)
    setRepeatCadenceInDays(0)
  }, [])

  useEffect(() => {
    if (!open) {
      reset()
    }
  }, [open, reset])

  const close = () => {
    if (saving) {
      return
    }
    onClose()
  }

  const alreadyOpen = survey !== '' && openQuestionnaires.includes(survey)
  const canSubmit = survey !== '' && !alreadyOpen && !saving
  const assignLabel = repeatCadenceInDays ? 'Assign repeating survey' : 'Assign non-repeating survey'

  const submit = async () => {
    if (!survey || alreadyOpen || saving) {
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onAssign(survey, repeatCadenceInDays || undefined)
      onClose()
    } catch (failure) {
      setError(describeFailure('Failed to assign this survey.', failure))
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogHeader title="Assign symptom survey" onClose={close} />

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Select
          size="small"
          fullWidth
          displayEmpty
          value={survey}
          onChange={(event: SelectChangeEvent<AssignableQuestionnaire | ''>) => {
            const next = event.target.value as AssignableQuestionnaire | ''
            setSurvey(next)
            setRepeatCadenceInDays(next ? DEFAULT_REPEAT_CADENCE_IN_DAYS[next] : 0)
          }}
        >
          <MenuItem value="" disabled>
            Select a survey
          </MenuItem>
          {BUILT_IN_QUESTIONNAIRES.map((option) => (
            <MenuItem key={option} value={option} disabled={openQuestionnaires.includes(option)}>
              {QUESTIONNAIRE_LABELS[option]}
              {openQuestionnaires.includes(option) ? ' (already assigned)' : ''}
            </MenuItem>
          ))}
        </Select>
        <TextField
          select
          size="small"
          label="Repeat"
          value={repeatCadenceInDays}
          disabled={saving}
          onChange={(event) => setRepeatCadenceInDays(Number(event.target.value))}
          helperText={
            repeatCadenceInDays
              ? `The first survey is available now. Surveys repeat every ${formatDays(
                  repeatCadenceInDays
                )} from the initial assignment. Unassign to stop repeats.`
              : 'The survey is assigned once.'
          }
        >
          <MenuItem value={0}>Does not repeat</MenuItem>
          <MenuItem value={1}>Every day</MenuItem>
          <MenuItem value={7}>Every week</MenuItem>
          <MenuItem value={14}>Every 2 weeks</MenuItem>
          <MenuItem value={28}>Every 4 weeks</MenuItem>
        </TextField>
        {error && (
          <Typography variant="body2" color="error">
            <FailureMessage failure={error} />
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={close} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!canSubmit} onClick={submit}>
          {saving ? 'Assigning…' : assignLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddSymptomSurveyDialog
